/** Query keys, query options and mutations — the only place components learn about endpoints. */
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, isApiError, type Schemas, setCsrfToken, unwrap } from "./client";

export type Session = Schemas["SessionOut"];
export type User = Schemas["UserOut"];
export type Config = Schemas["ConfigOut"];
export type ConfigFormat = Schemas["ConfigFormat"];
export type ConfigStatus = Schemas["ConfigStatus"];
export type MetricsRange = Schemas["MetricsRange"];
export type AuditAction = Schemas["AuditAction"];
export type ConfigRequestStatus = Schemas["ConfigRequestStatus"];

export const keys = {
  session: ["session"] as const,
  devices: ["devices"] as const,
  announcements: ["announcements"] as const,
  meta: ["meta"] as const,
  configs: ["configs"] as const,
  configContent: (id: number) => ["configs", id, "content"] as const,
  admin: ["admin"] as const,
  dashboard: ["admin", "dashboard"] as const,
  users: (query: string) => ["admin", "users", query] as const,
  user: (id: number) => ["admin", "user", id] as const,
  allConfigs: (filter: AdminConfigFilter) => ["admin", "configs", filter] as const,
  invites: ["admin", "invites"] as const,
  registration: ["registration"] as const,
  adminAnnouncements: ["admin", "announcements"] as const,
  configRequests: (status: ConfigRequestStatus | null) =>
    ["admin", "config-requests", status] as const,
  pendingRequests: ["admin", "config-requests", "pending-count"] as const,
  audit: (actions: readonly AuditAction[] | null) => ["admin", "audit", actions] as const,
  hostMetrics: (range: MetricsRange) => ["admin", "metrics", "host", range] as const,
  capacity: ["admin", "metrics", "capacity"] as const,
  top: (range: MetricsRange) => ["admin", "metrics", "top", range] as const,
  usage: (range: MetricsRange) => ["admin", "metrics", "usage", range] as const,
  domains: (range: MetricsRange, userId: number | null, configId: number | null) =>
    ["admin", "metrics", "domains", range, userId, configId] as const,
  userTraffic: (id: number, range: MetricsRange) =>
    ["admin", "user", id, "traffic", range] as const,
  myTraffic: (range: MetricsRange) => ["configs", "traffic", range] as const,
};

// ---- session ---------------------------------------------------------------------------------

/** Resolves to `null` for anonymous visitors instead of failing. */
export const sessionQuery = queryOptions({
  queryKey: keys.session,
  queryFn: async (): Promise<Session | null> => {
    try {
      const session = await unwrap(api.GET("/api/auth/session"));
      setCsrfToken(session.csrf_token);
      return session;
    } catch (error) {
      if (isApiError(error, "authentication_required")) {
        setCsrfToken(null);
        return null;
      }
      throw error;
    }
  },
  staleTime: 5 * 60_000,
});

function useSignedIn() {
  const client = useQueryClient();
  return (session: Session) => {
    setCsrfToken(session.csrf_token);
    client.clear();
    client.setQueryData(keys.session, session);
  };
}

export function useLogin() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (body: Schemas["LoginIn"]) => unwrap(api.POST("/api/auth/login", { body })),
    onSuccess: signedIn,
  });
}

export function useRegister() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (body: Schemas["RegisterIn"]) => unwrap(api.POST("/api/auth/register", { body })),
    onSuccess: signedIn,
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/auth/logout")),
    onSettled: () => {
      setCsrfToken(null);
      client.clear();
      client.setQueryData(keys.session, null);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: Schemas["ChangePasswordIn"]) =>
      unwrap(api.POST("/api/auth/password", { body })),
    // The server signs out every other device on a password change.
    onSuccess: useInvalidate(keys.devices),
  });
}

export const announcementsQuery = queryOptions({
  queryKey: keys.announcements,
  queryFn: () => unwrap(api.GET("/api/me/announcements")),
  refetchInterval: 5 * 60_000,
});

export const devicesQuery = queryOptions({
  queryKey: keys.devices,
  queryFn: () => unwrap(api.GET("/api/auth/sessions")),
});

export function useRevokeDevice() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/api/auth/sessions/{public_id}", { params: { path: { public_id: id } } })),
    onSettled: useInvalidate(keys.devices),
  });
}

export function useRevokeOtherDevices() {
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/auth/sessions/revoke-others")),
    onSuccess: useInvalidate(keys.devices),
  });
}

export const registrationQuery = queryOptions({
  queryKey: keys.registration,
  queryFn: () => unwrap(api.GET("/api/auth/registration")),
});

export async function checkInvite(code: string): Promise<boolean> {
  const result = await unwrap(api.GET("/api/auth/invites/{code}", { params: { path: { code } } }));
  return result.valid;
}

// ---- own configs -----------------------------------------------------------------------------

export const metaQuery = queryOptions({
  queryKey: keys.meta,
  queryFn: () => unwrap(api.GET("/api/meta")),
  staleTime: 10 * 60_000,
});

export const configsQuery = queryOptions({
  queryKey: keys.configs,
  queryFn: () => unwrap(api.GET("/api/configs")),
  // Keep "online" and traffic fresh while the page is open; the server syncs every 90 s.
  refetchInterval: 60_000,
});

export const configContentQuery = (id: number) =>
  queryOptions({
    queryKey: keys.configContent(id),
    queryFn: () =>
      unwrap(api.GET("/api/configs/{config_id}/content", { params: { path: { config_id: id } } })),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60_000,
  });

function useInvalidate(...queryKeys: readonly (readonly unknown[])[]) {
  const client = useQueryClient();
  return () => Promise.all(queryKeys.map((queryKey) => client.invalidateQueries({ queryKey })));
}

export function useCreateConfig() {
  return useMutation({
    mutationFn: (body: Schemas["CreateConfigIn"]) => unwrap(api.POST("/api/configs", { body })),
    onSuccess: useInvalidate(keys.configs, keys.admin),
  });
}

export function useRequestConfig() {
  return useMutation({
    mutationFn: (comment: string | null) =>
      unwrap(api.POST("/api/config-requests", { body: { comment } })),
    onSuccess: useInvalidate(keys.configs, keys.admin),
  });
}

export function useRenameConfig(id: number) {
  return useMutation({
    mutationFn: (name: string) =>
      unwrap(
        api.PATCH("/api/configs/{config_id}", {
          params: { path: { config_id: id } },
          body: { name },
        }),
      ),
    onSuccess: useInvalidate(keys.configs, keys.admin),
  });
}

export function useReissueConfig(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (format: ConfigFormat | null) =>
      unwrap(
        api.POST("/api/configs/{config_id}/reissue", {
          params: { path: { config_id: id } },
          body: { format },
        }),
      ),
    onSuccess: async () => {
      client.removeQueries({ queryKey: keys.configContent(id) });
      await client.invalidateQueries({ queryKey: keys.configs });
      await client.invalidateQueries({ queryKey: keys.admin });
    },
  });
}

export function useDeleteConfig(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(api.DELETE("/api/configs/{config_id}", { params: { path: { config_id: id } } })),
    onSuccess: async () => {
      client.removeQueries({ queryKey: keys.configContent(id) });
      await client.invalidateQueries({ queryKey: keys.configs });
      await client.invalidateQueries({ queryKey: keys.admin });
    },
  });
}

// ---- admin -----------------------------------------------------------------------------------

export type AdminConfigFilter = { status?: ConfigStatus; unowned?: boolean };

export const dashboardQuery = queryOptions({
  queryKey: keys.dashboard,
  queryFn: () => unwrap(api.GET("/api/admin/dashboard")),
  refetchInterval: 60_000,
});

export const usersQuery = (query: string) =>
  queryOptions({
    queryKey: keys.users(query),
    queryFn: () =>
      unwrap(api.GET("/api/admin/users", { params: { query: query ? { query } : {} } })),
  });

export const userQuery = (id: number) =>
  queryOptions({
    queryKey: keys.user(id),
    queryFn: () =>
      unwrap(api.GET("/api/admin/users/{user_id}", { params: { path: { user_id: id } } })),
  });

export const allConfigsQuery = (filter: AdminConfigFilter) =>
  queryOptions({
    queryKey: keys.allConfigs(filter),
    queryFn: () =>
      unwrap(
        api.GET("/api/admin/configs", {
          params: {
            query: {
              ...(filter.status ? { status: [filter.status] } : {}),
              ...(filter.unowned ? { unowned: true } : {}),
              limit: 200,
            },
          },
        }),
      ),
  });

export const invitesQuery = queryOptions({
  queryKey: keys.invites,
  queryFn: () => unwrap(api.GET("/api/admin/invites")),
});

export const configRequestsQuery = (status: ConfigRequestStatus | null) =>
  queryOptions({
    queryKey: keys.configRequests(status),
    queryFn: () =>
      unwrap(
        api.GET("/api/admin/config-requests", {
          params: { query: status ? { status } : {} },
        }),
      ),
  });

/** Drives the badge in the admin menu, so it polls on every admin page. */
export const pendingRequestsQuery = queryOptions({
  queryKey: keys.pendingRequests,
  queryFn: () => unwrap(api.GET("/api/admin/config-requests/pending-count")),
  refetchInterval: 60_000,
});

export const auditQuery = (actions: readonly AuditAction[] | null) =>
  queryOptions({
    queryKey: keys.audit(actions),
    queryFn: () =>
      unwrap(
        api.GET("/api/admin/audit", {
          params: { query: { limit: 200, ...(actions ? { action: [...actions] } : {}) } },
        }),
      ),
  });

// The sampler writes a point a minute: refreshing faster only redraws the same chart.
const METRICS_REFRESH = 60_000;

export const hostMetricsQuery = (range: MetricsRange) =>
  queryOptions({
    queryKey: keys.hostMetrics(range),
    queryFn: () => unwrap(api.GET("/api/admin/metrics/host", { params: { query: { range } } })),
    refetchInterval: METRICS_REFRESH,
  });

export const capacityQuery = queryOptions({
  queryKey: keys.capacity,
  queryFn: () => unwrap(api.GET("/api/admin/metrics/capacity")),
  refetchInterval: 5 * METRICS_REFRESH,
});

export const topConfigsQuery = (range: MetricsRange) =>
  queryOptions({
    queryKey: keys.top(range),
    queryFn: () => unwrap(api.GET("/api/admin/metrics/top", { params: { query: { range } } })),
    refetchInterval: METRICS_REFRESH,
  });

export const usageQuery = (range: MetricsRange) =>
  queryOptions({
    queryKey: keys.usage(range),
    queryFn: () => unwrap(api.GET("/api/admin/metrics/usage", { params: { query: { range } } })),
    refetchInterval: METRICS_REFRESH,
  });

export const domainsQuery = (
  range: MetricsRange,
  scope: { userId?: number; configId?: number } = {},
) =>
  queryOptions({
    queryKey: keys.domains(range, scope.userId ?? null, scope.configId ?? null),
    queryFn: () =>
      unwrap(
        api.GET("/api/admin/metrics/domains", {
          params: {
            query: {
              range,
              ...(scope.userId === undefined ? {} : { user_id: scope.userId }),
              ...(scope.configId === undefined ? {} : { config_id: scope.configId }),
            },
          },
        }),
      ),
    refetchInterval: METRICS_REFRESH,
  });

export const userTrafficQuery = (id: number, range: MetricsRange) =>
  queryOptions({
    queryKey: keys.userTraffic(id, range),
    queryFn: () =>
      unwrap(
        api.GET("/api/admin/users/{user_id}/traffic", {
          params: { path: { user_id: id }, query: { range } },
        }),
      ),
  });

/** Under `configs`, so deleting or reissuing a config refreshes the owner's numbers too. */
export const myTrafficQuery = (range: MetricsRange) =>
  queryOptions({
    queryKey: keys.myTraffic(range),
    queryFn: () => unwrap(api.GET("/api/me/traffic", { params: { query: { range } } })),
    refetchInterval: METRICS_REFRESH,
  });

function useAdminMutation<TVariables, TData>(fn: (variables: TVariables) => Promise<TData>) {
  return useMutation({ mutationFn: fn, onSuccess: useInvalidate(keys.admin, keys.configs) });
}

const userPath = (id: number) => ({ params: { path: { user_id: id } } });
const configPath = (id: number) => ({ params: { path: { config_id: id } } });

export const useBlockUser = () =>
  useAdminMutation((id: number) =>
    unwrap(api.POST("/api/admin/users/{user_id}/block", userPath(id))),
  );

export const useUnblockUser = () =>
  useAdminMutation((id: number) =>
    unwrap(api.POST("/api/admin/users/{user_id}/unblock", userPath(id))),
  );

export const useResetPassword = () =>
  useMutation({
    mutationFn: (id: number) =>
      unwrap(api.POST("/api/admin/users/{user_id}/password-reset", userPath(id))),
  });

export const useSetLimit = () =>
  useAdminMutation(({ id, limit }: { id: number; limit: number }) =>
    unwrap(
      api.PUT("/api/admin/users/{user_id}/limit", {
        ...userPath(id),
        body: { self_config_limit: limit },
      }),
    ),
  );

export const useIssueConfig = () =>
  useAdminMutation(({ userId, format }: { userId: number; format: ConfigFormat }) =>
    unwrap(
      api.POST("/api/admin/users/{user_id}/configs", { ...userPath(userId), body: { format } }),
    ),
  );

export const useRevokeConfig = () =>
  useAdminMutation((id: number) =>
    unwrap(api.POST("/api/admin/configs/{config_id}/revoke", configPath(id))),
  );

export const useAssignConfig = () =>
  useAdminMutation(({ id, userId }: { id: number; userId: number | null }) =>
    unwrap(
      api.PUT("/api/admin/configs/{config_id}/owner", {
        ...configPath(id),
        body: { user_id: userId },
      }),
    ),
  );

export const useSyncNow = () => useAdminMutation(() => unwrap(api.POST("/api/admin/sync")));

export const useSetInviteRequired = () =>
  useMutation({
    mutationFn: (invite_required: boolean) =>
      unwrap(api.PUT("/api/admin/registration", { body: { invite_required } })),
    onSuccess: useInvalidate(keys.registration),
  });

export const useCreateInvite = () =>
  useAdminMutation((body: Schemas["InviteIn"]) => unwrap(api.POST("/api/admin/invites", { body })));

export const useApproveConfigRequest = () =>
  useAdminMutation((id: number) =>
    unwrap(
      api.POST("/api/admin/config-requests/{request_id}/approve", {
        params: { path: { request_id: id } },
      }),
    ),
  );

export const useRejectConfigRequest = () =>
  useAdminMutation(({ id, comment }: { id: number; comment: string | null }) =>
    unwrap(
      api.POST("/api/admin/config-requests/{request_id}/reject", {
        params: { path: { request_id: id } },
        body: { comment },
      }),
    ),
  );

export const adminAnnouncementsQuery = queryOptions({
  queryKey: keys.adminAnnouncements,
  queryFn: () => unwrap(api.GET("/api/admin/announcements")),
});

export function useCreateAnnouncement() {
  return useMutation({
    mutationFn: (body: Schemas["AnnouncementIn"]) =>
      unwrap(api.POST("/api/admin/announcements", { body })),
    onSuccess: useInvalidate(keys.adminAnnouncements, keys.announcements),
  });
}

export function useDeactivateAnnouncement() {
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(
        api.POST("/api/admin/announcements/{announcement_id}/deactivate", {
          params: { path: { announcement_id: id } },
        }),
      ),
    onSuccess: useInvalidate(keys.adminAnnouncements, keys.announcements),
  });
}

export const useSetNote = () =>
  useAdminMutation(({ id, note }: { id: number; note: string | null }) =>
    unwrap(api.PUT("/api/admin/users/{user_id}/note", { ...userPath(id), body: { note } })),
  );

export const useSetRole = () =>
  useAdminMutation(({ id, role }: { id: number; role: User["role"] }) =>
    unwrap(api.PUT("/api/admin/users/{user_id}/role", { ...userPath(id), body: { role } })),
  );

export const useSignOutUser = () =>
  useAdminMutation((id: number) =>
    unwrap(api.POST("/api/admin/users/{user_id}/sign-out", userPath(id))),
  );

/** No automatic refetch: the open card would 404 before the caller has navigated away. */
export function useDeleteUser() {
  const client = useQueryClient();
  return {
    ...useMutation({
      mutationFn: ({ id, confirmUsername }: { id: number; confirmUsername: string }) =>
        unwrap(
          api.POST("/api/admin/users/{user_id}/delete", {
            ...userPath(id),
            body: { confirm_username: confirmUsername },
          }),
        ),
    }),
    refresh: () => client.invalidateQueries({ queryKey: keys.admin }),
  };
}

export const useDeactivateInvite = () =>
  useAdminMutation((id: number) =>
    unwrap(
      api.POST("/api/admin/invites/{invite_id}/deactivate", {
        params: { path: { invite_id: id } },
      }),
    ),
  );
